import { useMemo } from 'react';
import { Box, Chip, Drawer, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { RxCross2 } from 'react-icons/rx';
import DataTable from 'components/Common/DataTable';
import { useGetA2pCampaignLinkableNumbers } from '@/apis/business/setting/phoneNumbers/hook';
import { A2pCampaign, A2pLinkableNumber } from '@/apis/business/setting/phoneNumbers/types';
import { A2P_POLL_INTERVAL_MS } from './a2pConstants';
import {
  LINK_STATUS_CHIP,
  formatA2pAddedOn,
  formatA2pNumber,
  formatA2pProvider,
  isLinkPending
} from './a2pNumberFormat';

type Props = {
  open: boolean;
  campaign: A2pCampaign | null;
  onClose: () => void;
};

const BORDER_COLOR = '#E5E7EB';

export default function LinkedNumbersDrawer({ open, campaign, onClose }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { data, isLoading } = useGetA2pCampaignLinkableNumbers(open ? campaign?.id : null, {
    refetchInterval: (query) => (query.state.data?.data?.some(isLinkPending) ? A2P_POLL_INTERVAL_MS : false)
  });

  const rows = useMemo(() => (data?.data || []).filter((row) => Boolean(row.link_status)), [data]);

  const columns = useMemo<ColumnDef<A2pLinkableNumber>[]>(
    () => [
      {
        header: 'NUMBER',
        accessorKey: 'phone_number',
        cell: ({ row }) => formatA2pNumber(row.original.phone_number)
      },
      {
        header: 'PROVIDER',
        accessorKey: 'provider',
        cell: ({ row }) => formatA2pProvider(row.original.provider)
      },
      {
        header: 'ADDED ON',
        accessorKey: 'added_on',
        cell: ({ row }) => formatA2pAddedOn(row.original.added_on)
      },
      {
        header: 'STATUS',
        accessorKey: 'link_status',
        cell: ({ row }) => {
          const status = row.original.link_status;
          if (!status) return '—';
          const chip = LINK_STATUS_CHIP[status];
          return (
            <Chip
              label={chip.label}
              size='small'
              sx={{
                bgcolor: chip.bg,
                color: chip.color,
                fontWeight: 500,
                fontSize: 12,
                borderRadius: '999px',
                height: 24
              }}
            />
          );
        }
      }
    ],
    []
  );

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 900,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2.5}
        borderBottom={`1px solid ${BORDER_COLOR}`}
      >
        <Typography fontSize={18} fontWeight={500} color='text.primary'>
          Linked Numbers
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close' sx={{ color: 'text.primary' }}>
          <RxCross2 size={20} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={3} py={3}>
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          isHeaderSticky={false}
          enableColumnResizing={false}
          containerStyles={{ border: `1px solid ${BORDER_COLOR}`, borderRadius: '8px', overflow: 'hidden' }}
        />
      </Box>
    </Drawer>
  );
}
